import { useEffect, useMemo, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import {
  AppShell,
  BusinessIcon,
  CreditCardIcon,
  InventoryIcon,
  MailIcon,
  SettingsIcon,
  type NavGroup,
} from '@platform/design-system';
import type { MenuItem } from '@platform/authz';
import { RouterLink, useSession, useVisibleMenu } from '@platform/app-kit';
import { ANNOUNCEMENTS_ROUTE, PACKAGES_ROUTE, TENANTS_ROUTE, USAGE_ROUTE } from './routes.ts';

type NavEntry = MenuItem & { label: string; icon: ReactNode; href: string };

/**
 * The platform-staff console shell: the tenant shell minus what a two-destination console
 * does not need (no ⌘K, no bottom tab bar — a menu button opens the drawer on phones; §10).
 * Fixed staff brand. Navigation is `visibleMenu` over the contract routes.
 */
export function AdminShell() {
  const { principal, signOut } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const page = location.pathname.startsWith('/packages')
      ? 'Packages'
      : location.pathname.startsWith('/tenants')
        ? 'Tenants'
        : location.pathname.startsWith('/account')
          ? 'Account'
          : location.pathname.startsWith('/usage')
            ? 'Assistant usage'
            : location.pathname.startsWith('/notices')
              ? 'Notices'
              : 'Console';
    document.title = `${page} · Bigweld admin`;
  }, [location.pathname]);

  const catalogue = useMemo<NavEntry[]>(
    () => [
      {
        id: 'tenants',
        label: 'Tenants',
        icon: <BusinessIcon />,
        href: '/tenants',
        auth: TENANTS_ROUTE.auth,
        action: TENANTS_ROUTE.action,
      },
      {
        id: 'packages',
        label: 'Packages',
        icon: <InventoryIcon />,
        href: '/packages',
        auth: PACKAGES_ROUTE.auth,
        action: PACKAGES_ROUTE.action,
      },
      {
        id: 'usage',
        label: 'Assistant usage',
        icon: <CreditCardIcon />,
        href: '/usage',
        auth: USAGE_ROUTE.auth,
        action: USAGE_ROUTE.action,
      },
      {
        id: 'notices',
        label: 'Notices',
        icon: <MailIcon />,
        href: '/notices',
        auth: ANNOUNCEMENTS_ROUTE.auth,
        action: ANNOUNCEMENTS_ROUTE.action,
      },
    ],
    [],
  );
  const visible = useVisibleMenu(catalogue);

  if (!principal) return null;

  const is = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const nav: NavGroup[] = [
    {
      id: 'core',
      items: visible.map((item) => ({
        id: item.id,
        label: item.label,
        icon: item.icon,
        href: item.href,
        active: is(item.href),
      })),
    },
  ];

  return (
    <AppShell
      brand={{ name: 'Bigweld admin', homeHref: '/tenants' }}
      nav={nav}
      bottomTabs={[]}
      account={{
        name: principal.name,
        email: principal.email,
        items: [
          {
            id: 'account',
            label: 'Account',
            icon: <SettingsIcon />,
            href: '/account',
            active: is('/account'),
          },
        ],
        onSignOut: () => {
          void signOut().then(() => navigate('/login', { replace: true }));
        },
      }}
      LinkComponent={RouterLink}
    >
      <Outlet />
    </AppShell>
  );
}
